import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { useLocation } from 'react-router'

import Loader from 'components/ui/loader/Loader'
import * as fromActions from 'store/actions/lessons.action'
import { IMainState } from 'store/states'
import ConfirmationModal from 'components/ui/modals/confirmation/ConfirmationModal'
import { useLesson } from 'contexts'
import { BlockTypes } from 'enum/block-type.enum'
import { EvaluationStatesEnum } from 'enum/evaluation-states.enum'
import Evaluation from './components/evaluation/Evaluation'
import { GenerateContent } from './GenerateContent'

interface IContentBlock {
  lessonId: any
  blockInstances: any
  setMandatoriesRef?: (elements: (HTMLElement | null)[]) => void
  canEdit: boolean
  onEvaluation: boolean
  setOnEvaluation: any
}

export const ContentBlock: React.FC<IContentBlock> = ({
  lessonId,
  blockInstances,
  setMandatoriesRef,
  canEdit,
  onEvaluation,
  setOnEvaluation,
}) => {
  const { currentLessonContent, loading } = useSelector(
    ({ lmsLessons }: IMainState) => lmsLessons
  )
  const course = useSelector(({ courses }: IMainState) => courses.course)
  const dispatch = useDispatch()
  const { next, setSubmit } = useLesson()
  const { t } = useTranslation()
  const count = useRef(0)
  const location = useLocation()
  const [showConfirmationModal, setShowConfirmationModal] = useState(false)

  const reload = useCallback(() => {
    blockInstances &&
      lessonId &&
      dispatch({
        type: fromActions.LESSONS_GET_LESSON_CONTENT,
        payload: { blockInstances, lessonId },
      })
  }, [blockInstances, dispatch, lessonId])

  useEffect(() => {
    reload()
  }, [reload])

  const refArray = useRef([])

  const handleConfirmationModal = useCallback(
    (attempts: number) => {
      attempts > 0 ? setShowConfirmationModal(true) : setOnEvaluation(true)
    },
    [setOnEvaluation]
  )

  useLayoutEffect(() => {
    setMandatoriesRef?.(refArray.current)
    return () => {
      setMandatoriesRef?.([])
    }
  }, [setMandatoriesRef])

  const evaluationData = useMemo(() => {
    if (currentLessonContent.length > 0) {
      const evaluation = currentLessonContent.find(
        (block) => block.type === BlockTypes.BlockEvaluation
      )
      return evaluation
    }
  }, [currentLessonContent])

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [onEvaluation])

  const handleAcceptConfirmation = () => {
    setShowConfirmationModal(false)
    setOnEvaluation(true)
  }

  window.onbeforeunload = (e: any) => {
    if (onEvaluation) {
      e.preventDefault()
      e.returnValue = t('lessons.modals.repeatEvaluation.leave')
    }
  }

  const courseId = course.id

  return (
    <>
      {loading && !onEvaluation ? (
        <Loader color />
      ) : (
        !!courseId && (
          <>
            {!onEvaluation ? (
              <GenerateContent
                lessonContent={currentLessonContent}
                setOnEvaluation={handleConfirmationModal}
                courseId={courseId}
                {...{
                  refArray,
                  canEdit,
                  count,
                  location,
                  reload,
                  next,
                  lessonId,
                }}
              />
            ) : (
              <Evaluation
                state={EvaluationStatesEnum.inProcess}
                courseId={courseId}
                blockInstances={blockInstances}
                lessonId={lessonId}
                evaluationId={(evaluationData && evaluationData.id) || 0}
                canEdit={canEdit}
                setOnEvaluation={() => setOnEvaluation(false)}
                onSuccess={() => setSubmit(false)}
              />
            )}
            <ConfirmationModal
              show={showConfirmationModal}
              title=""
              text={t('lessons.modals.repeatEvaluation.message')}
              acceptButtonText={t('components.modal.buttons.accept')}
              onAccept={handleAcceptConfirmation}
              onClose={() => setShowConfirmationModal(false)}
            />
          </>
        )
      )}
    </>
  )
}
export default ContentBlock
